import type { BridgeContext, BridgeFeatures, BridgeInvokeHandler } from './types';
import type { TAnyBridgeMethod, TBridgeMiddleware } from './methods/types';
import { bridgeMethods as baseBridgeMethods } from './methods';
import { withMetrics } from './middleware/withMetrics';

/**
 * Middlewares applied to every bridge method across all runtimes.
 * Order is outermost-first: the first entry wraps all subsequent ones.
 */
const DEFAULT_MIDDLEWARES: TBridgeMiddleware[] = [withMetrics];

const applyMiddleware = (
	method: TAnyBridgeMethod,
	methodName: string,
	middlewares: TBridgeMiddleware[],
): TAnyBridgeMethod => middlewares.reduceRight((m, mw) => mw(m, methodName), method);

/**
 * Builds the `BridgeFeatures` map expected by `createWorkerBridge` from the
 * registered `bridgeMethods`.
 *
 * Each method is wrapped so that it receives the `data` field from the bridge
 * message payload, and returns `true` as a default acknowledgement when the
 * method returns `undefined`.
 *
 * Product-provided callbacks (e.g. `openModal`, `showFlag`) are accessed via
 * `getBridgeContext().bridgeConfig` at invocation time rather than being passed
 * in directly, so the bridge features map never needs to be rebuilt when the
 * product's bridge prop changes.
 *
 * @param adaptBridgeMethods - Optional runtime-specific method adapter, e.g. to add extra methods or override existing ones.
 * @param adaptMiddlewares - Optional runtime-specific middleware adapter. Receives the default
 *   middleware chain and returns the final chain, so callers can add middleware at any position
 *   (outside or inside the defaults), reorder, or replace them. The returned array is applied
 *   outermost-first: the first entry wraps all subsequent ones, which wrap the bridge method.
 *   For example, `(defaults) => [outerMw, ...defaults]` runs `outerMw` outside `withMetrics`, so
 *   `withMetrics` observes any context/payload changes `outerMw` makes.
 */
export const createBridgeFeatures = (
	getBridgeContext: () => BridgeContext,
	adaptBridgeMethods?: (
		methods: Record<string, TAnyBridgeMethod>,
	) => Record<string, TAnyBridgeMethod>,
	adaptMiddlewares: (defaults: TBridgeMiddleware[]) => TBridgeMiddleware[] = (defaults) => defaults,
): BridgeFeatures => {
	const middlewares = adaptMiddlewares([...DEFAULT_MIDDLEWARES]);
	const bridgeMethods = adaptBridgeMethods
		? adaptBridgeMethods(baseBridgeMethods)
		: baseBridgeMethods;
	return Object.entries(bridgeMethods).reduce((features, [methodName, method]) => {
		const wrapped = applyMiddleware(method as TAnyBridgeMethod, methodName, middlewares);
		features[methodName] = convertToBridgeInvokeHandler((payload: unknown) => {
			// Call getBridgeContext() at invocation time, not creation time, so each
			// bridge call always uses the latest context rather than a stale snapshot.
			return wrapped(getBridgeContext(), payload);
		});
		return features;
	}, {} as BridgeFeatures);
};

const convertToBridgeInvokeHandler = (
	method?: (data: unknown) => Promise<unknown>,
): BridgeInvokeHandler => {
	return async ({ data }) => {
		if (method) {
			return (await method(data)) ?? true;
		}
		return false;
	};
};
